import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../../../lib/utils';
import { usePrefersReducedMotion } from '../theme/hooks';
import { Card, Skel } from './parts';
import { ago, failed, int, Loaded, pctChange } from './api';

type Insight = { big: string; text: string };

/** Short, true sentences built from the same summary the other cards show. */
const buildInsights = (d: Loaded): Insight[] => {
  const out: Insight[] = [];
  const days = d.range.days;
  if (!failed(d.chat)) {
    const c = d.chat;
    if (c.now.waiting > 0) out.push({ big: String(c.now.waiting), text: `Chat${c.now.waiting === 1 ? ' is' : 's are'} waiting for a person right now.${c.online ? '' : ' Live chat is set to offline.'}` });
    const pct = pctChange(c.totals.chats, c.totals.prevChats);
    if (c.totals.chats && pct !== null) out.push({ big: `${pct >= 0 ? '+' : '−'}${Math.abs(pct).toFixed(0)}%`, text: `Chats against the previous ${days} days: ${int(c.totals.chats)} now, ${int(c.totals.prevChats)} before.` });
    if (c.totals.chats) {
      const share = Math.round((c.totals.team / c.totals.chats) * 100);
      out.push({ big: `${share}%`, text: share ? `Of chats in this period were handed to the team. The bot answered the rest.` : `Of chats needed a person. The bot handled every one.` });
    }
  }
  if (!failed(d.visitors) && d.visitors.connected) {
    const v = d.visitors;
    const pct = pctChange(v.totals.sessions, v.prevTotals.sessions);
    if (pct !== null) out.push({ big: `${pct >= 0 ? '+' : '−'}${Math.abs(pct).toFixed(0)}%`, text: `Site sessions against the previous ${days} days: ${int(v.totals.sessions)} now, ${int(v.prevTotals.sessions)} before.` });
    const top = v.topPages[0];
    if (top && v.totals.pageviews) out.push({ big: `${Math.round((top.views / v.totals.pageviews) * 100)}%`, text: `Of page views landed on ${top.path === '/' ? 'the home page' : top.path}, the most visited page.` });
    // Older API responses lack the fields below, so each is guarded.
    const phone = v.devices?.find((x) => x.device === 'Mobile');
    if (phone && v.totals.sessions) out.push({ big: `${Math.round((phone.sessions / v.totals.sessions) * 100)}%`, text: `Of visits were on a phone. The site's mobile layout matters as much as desktop.` });
    const channel = v.channels?.[0];
    const chTotal = v.channels?.reduce((a, c) => a + c.sessions, 0) ?? 0;
    if (channel && chTotal) out.push({ big: `${Math.round((channel.sessions / chTotal) * 100)}%`, text: `Of visits came from ${channel.channel.toLowerCase()} traffic, the largest channel.` });
    if (v.health?.lcp) out.push({ big: `${(v.health.lcp / 1000).toFixed(1)}s`, text: `For a typical page's main content to appear. Google counts 2.5 s or less as good.` });
  }
  if (!failed(d.sync)) {
    const last = d.sync.runs.find((r) => r.status === 'success');
    if (last) out.push({ big: ago(last.finishedAt || last.startedAt).replace(' ago', ''), text: `Since the Ralawise product feed last synced. ${int(d.sync.catalogue.liveStyles)} styles are live.` });
  }
  return out;
};

export const InsightsCard: React.FC<{ data: Loaded | null }> = ({ data }) => {
  const reduce = usePrefersReducedMotion();
  const items = data ? buildInsights(data) : [];
  const [cur, setCur] = useState(0);
  const x0 = useRef<number | null>(null);
  const n = items.length;
  useEffect(() => {
    if (cur >= n) setCur(0);
  }, [n, cur]);
  const go = (i: number) => setCur(((i % n) + n) % n);

  return (
    <Card
      i={1}
      area="ins"
      className="oa-insight"
      title="Worth knowing"
      icon={
        <svg className="oa-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="var(--oa-line-2)" strokeWidth="2" />
          <path d="M12 3a9 9 0 0 1 9 9" stroke="var(--oa-accent)" strokeWidth="2" strokeLinecap="round" />
        </svg>
      }
    >
      <div
        className="oa-slides touch-pan-y"
        aria-live="polite"
        onPointerDown={(e) => {
          if (e.pointerType !== 'mouse') x0.current = e.clientX;
        }}
        onPointerUp={(e) => {
          if (x0.current === null) return;
          const dx = e.clientX - x0.current;
          x0.current = null;
          if (Math.abs(dx) > 40) go(cur + (dx < 0 ? 1 : -1));
        }}
      >
        {!data ? (
          <div className="flex h-full flex-col justify-center gap-3">
            <Skel className="h-[52px] w-[160px]" />
            <Skel className="h-3 w-[80%]" />
          </div>
        ) : n === 0 ? (
          <div className="oa-slide is-on">
            <p>Nothing stands out yet. Figures appear here as chats, visits and syncs come in.</p>
          </div>
        ) : (
          items.map((it, i) => (
            <div key={i} className={cn('oa-slide', i === cur && 'is-on')}>
              <div className="oa-big text-[52px]">{it.big}</div>
              <p>{it.text}</p>
            </div>
          ))
        )}
      </div>
      {n > 1 ? (
        <div className="oa-dots" role="tablist" aria-label="Insights">
          {items.map((_, i) => (
            <button key={i} type="button" role="tab" aria-selected={i === cur} aria-label={`Insight ${i + 1}`} className={cn(i === cur && 'is-on')} onClick={() => go(i)}>
              <span>
                {/* key restarts the 6s progress bar each time this dot becomes current */}
                <i key={i === cur ? `on-${cur}` : 'off'} style={i === cur && reduce ? { width: '100%' } : undefined} onAnimationEnd={() => go(cur + 1)} />
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </Card>
  );
};
